/**
 * Shared parse helpers for item linkOut / text href on web share surfaces.
 * Twin of Containd `utils/itemLinkOut.ts` — keep formats in sync.
 */

export type ExternalLinkTarget = {
  kind: 'external';
  href: string;
};

export type ItemLinkTarget = {
  kind: 'item';
  ref: string;
};

export type ParsedLinkTarget = ExternalLinkTarget | ItemLinkTarget;

const ITEM_PREFIX = /^item:(.+)$/i;

/**
 * First-party hosts: skip leave-site confirm on Spaces freeze viewer.
 * Twin of Containd `TrustedRemoteHosts` TRUSTED_INSTALL_HOSTS + template-render
 * `linkOutParse.ts`.
 */
const TRUSTED_EXTERNAL_LINK_HOSTS = new Set([
  'containd.app',
  'containd.space',
  'containd.de',
  'containdapp.com',
  'flyingweasel.de',
  'madweasel.de',
  'madweaselapps.com',
  'madweaselapps.de',
  'nextlevelorder.com',
  'nextlevelorderengineering.com',
  'nloe.eu',
  'nxtlvlordereng.com',
  'shirome.de',
  'dschwenninger.de',
]);

export function formatItemLinkOut(collectionShareIdOrId: string | number): string {
  return `item:${String(collectionShareIdOrId).trim()}`;
}

export function parseLinkTarget(raw: string | null | undefined): ParsedLinkTarget | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const itemMatch = trimmed.match(ITEM_PREFIX);
  if (itemMatch) {
    const ref = itemMatch[1]?.trim();
    if (!ref) return null;
    return { kind: 'item', ref };
  }

  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:') ||
    lower.startsWith('tel:')
  ) {
    return { kind: 'external', href: trimmed };
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return { kind: 'external', href: trimmed };
  }

  return null;
}

export function isOpenEmptyViewerEnabled(value: boolean | null | undefined): boolean {
  return value !== false;
}

export function isAllowLayoutChangeEnabled(value: boolean | null | undefined): boolean {
  return value !== false;
}

/** Hostname without leading www; empty when not http(s). */
export function extractExternalLinkHost(href: string): string {
  try {
    const url = new URL(href.trim());
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
    return url.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

export function isTrustedExternalLinkHost(href: string): boolean {
  const host = extractExternalLinkHost(href);
  if (!host) return false;
  if (TRUSTED_EXTERNAL_LINK_HOSTS.has(host)) return true;
  for (const trusted of TRUSTED_EXTERNAL_LINK_HOSTS) {
    if (host.endsWith('.' + trusted)) return true;
  }
  return false;
}

/**
 * True when Spaces freeze viewer should show a leave-site confirm before navigating.
 * mailto:/tel: and first-party Containd hosts open without confirm.
 * Twin of template-render `linkOutParse.shouldConfirmExternalNavigation`.
 */
export function shouldConfirmExternalNavigation(href: string): boolean {
  const trimmed = href.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('mailto:') || lower.startsWith('tel:')) return false;
  if (lower.startsWith('http://') || lower.startsWith('https://')) {
    return !isTrustedExternalLinkHost(trimmed);
  }
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
}
